import React, { useState, useEffect } from 'react';
import {
    Typography,
    Button,
    Card,
    Form,
    Input,
    Select,
    Switch,
    Space,
    Spin,
    message,
    Divider
} from 'antd';
import {
    ArrowLeftOutlined,
    SaveOutlined,
    CodeOutlined,
    PlayCircleOutlined
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useGetScriptById } from '@/shared/api/client/context-script-controller';
import { ContextScriptEditor,ScriptTestingPanel,useContextScripts } from '@/modules/template';
import { useScriptTesting } from '../hooks/useScriptTesting';
import {ContextScriptDTO} from "@/shared/api/model";

const { Title } = Typography;
const { TextArea } = Input;

export const ContextScriptEditView: React.FC = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;

    const [script, setScript] = useState<ContextScriptDTO | null>(null);
    const [testingVisible, setTestingVisible] = useState(false);
    const [isFormReady, setIsFormReady] = useState(false);

    const { createScript, updateScript } = useContextScripts();
    const { validateScript, validationResult, isValidating } = useScriptTesting();

    // Load script data for edit mode - Fixed the API call
    const {
        data: scriptResponse,
        isLoading: isLoadingScript,
        error: scriptError
    } = useGetScriptById(id || '', {
        query: {
            enabled: isEditMode && !!id,
        }
    });

    // Handle script data loading and form population
    useEffect(() => {
        if (isEditMode && scriptResponse && !isFormReady) {
            const scriptData = scriptResponse;
            console.log('Received script data:', scriptData);

            setScript(scriptData);

            // Populate form with all fields
            const formValues = {
                name: scriptData.name || '',
                category: scriptData.category || '',
                active: scriptData.active !== undefined ? scriptData.active : true,
                version: scriptData.version || 1,
                description: scriptData.description || '',
                scriptContent: scriptData.scriptContent || '',
                sampleInput: scriptData.sampleInput || '',
                expectedOutput: scriptData.expectedOutput || ''
            };

            console.log('Setting form values:', formValues);
            form.setFieldsValue(formValues);
            setIsFormReady(true);
        }
    }, [scriptResponse, isEditMode, form, isFormReady]);

    // Handle loading error
    useEffect(() => {
        if (scriptError) {
            console.error('Error loading script:', scriptError);
            message.error('Failed to load script');
            navigate('/notifications/context-scripts');
        }
    }, [scriptError, navigate]);

    // Set initial form values for new scripts
    useEffect(() => {
        if (!isEditMode && !isFormReady) {
            const defaultValues = {
                active: true,
                version: 1,
                scriptContent: `/**
 * Transform input context before template processing
 * @param {Object} context - The original context object
 * @returns {Object} The transformed context object
 */
function transform(context) {
  // Your transformation logic here
  
  // Example: Add a formatted amount with currency symbol
  if (context.data && context.data.amount) {
    context.data.formattedAmount = '$' + context.data.amount.toFixed(2);
  }
  
  return context;
}`,
                sampleInput: JSON.stringify({
                    "user": {
                        "name": "John Doe",
                        "email": "john@example.com",
                        "phone": "+1234567890"
                    },
                    "data": {
                        "amount": 500,
                        "dueDate": "2025-07-20"
                    }
                }, null, 2)
            };

            form.setFieldsValue(defaultValues);
            setIsFormReady(true);
        }
    }, [isEditMode, form, isFormReady]);

    // Handle validation error highlighting
    const [errorLine, setErrorLine] = useState<number | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (validationResult && !validationResult.valid) {
            // Extract line number from error message if possible
            // Error messages often have format like "Syntax error at line 5, column 10"
            const lineMatch = validationResult.message?.match(/line (\d+)/i);
            setErrorLine(lineMatch ? parseInt(lineMatch[1]) : undefined);
            setErrorMessage(validationResult.message);
        } else {
            setErrorLine(undefined);
            setErrorMessage(undefined);
        }
    }, [validationResult]);

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            console.log('Form values to save:', values);

            // First validate the script
            const validationResult = await validateScript({
                scriptContent: values.scriptContent,
                testContextJson: values.sampleInput || '{}'
            });

            if (!validationResult?.valid) {
                message.error('Script validation failed. Please fix the errors before saving.');
                return;
            }

            // Create or update script
            if (isEditMode && id) {
                await updateScript({
                    ...values,
                    id
                });
                message.success('Script updated successfully');
            } else {
                await createScript(values);
                message.success('Script created successfully');
            }

            navigate('/notifications/context-scripts');
        } catch (err) {
            console.error('Error saving script:', err);
            message.error('Failed to save script');
        }
    };

    const handleTest = () => {
        // Always get the latest script content and sample input from the form
        const scriptContent = form.getFieldValue('scriptContent');
        const sampleInput = form.getFieldValue('sampleInput') || '{}';
        
        console.log('Validating script before testing:', { 
            scriptContentLength: scriptContent?.length, 
            sampleInputLength: sampleInput?.length 
        });
        
        // Validate script content before opening test panel
        if (!scriptContent || scriptContent.trim() === '') {
            message.error('Script content cannot be empty');
            return;
        }

        validateScript({scriptContent, testContextJson: sampleInput})
            .then(result => {
                if (result.valid) {
                    // Force form to store latest values before opening test panel
                    form.setFieldsValue({
                        scriptContent: scriptContent,
                        sampleInput: sampleInput
                    });
                    setTestingVisible(true);
                } else {
                    message.error(result.message || 'Script validation failed');
                }
            })
            .catch(error => {
                console.error('Error validating script:', error);
                message.error('Failed to validate script');
            });
    };

    // Handle successful test result
    const handleTestSuccess = (transformedContext: any) => {
        try {
            const formattedOutput = JSON.stringify(transformedContext, null, 2);
            form.setFieldsValue({ expectedOutput: formattedOutput });
            message.success('Test successful! Expected output updated.');
        } catch (error) {
            console.error('Error formatting output:', error);
        }
    };

    // Show loading spinner while loading script data
    if (isEditMode && (isLoadingScript || !isFormReady)) {
        return (
            <div className="flex justify-center items-center h-64">
                <Spin size="large" tip="Loading script..." />
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center mb-4">
                <Link to="/notifications/context-scripts">
                    <Button icon={<ArrowLeftOutlined />} className="mr-4">
                        Back to Scripts
                    </Button>
                </Link>
                <Title level={2} className="m-0">
                    {isEditMode ? `Edit Context Script: ${script?.name || ''}` : 'Create Context Script'}
                </Title>
            </div>

            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    preserve={false} // This helps with form state management
                >
                    <Form.Item
                        name="name"
                        label="Script Name"
                        rules={[{ required: true, message: 'Please enter script name' }]}
                    >
                        <Input placeholder="Enter script name" />
                    </Form.Item>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="category"
                            label="Category"
                            rules={[{ required: true, message: 'Please select a category' }]}
                        >
                            <Select placeholder="Select category">
                                <Select.Option value="LOAN">Loan</Select.Option>
                                <Select.Option value="SAVINGS">Savings</Select.Option>
                                <Select.Option value="ACCOUNT">Account</Select.Option>
                                <Select.Option value="SYSTEM">System</Select.Option>
                                <Select.Option value="OTHER">Other</Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="active"
                            label="Active"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="description"
                        label="Description"
                    >
                        <TextArea
                            rows={2}
                            placeholder="Enter script description (optional)"
                        />
                    </Form.Item>

                    <div className="mb-2 flex justify-between items-center">
                        <div>
                            <Title level={5} className="m-0">Script Content</Title>
                            {isValidating && <span className="text-gray-500 ml-2">Validating...</span>}
                        </div>
                        <Button
                            icon={<PlayCircleOutlined />}
                            onClick={handleTest}
                            disabled={isValidating}
                        >
                            Test Script
                        </Button>
                    </div>

                    <Form.Item
                        name="scriptContent"
                        rules={[{ required: true, message: 'Please enter script content' }]}
                        help={errorMessage}
                        validateStatus={errorMessage ? 'error' : undefined}
                    >
                        <ContextScriptEditor
                            language="javascript"
                            errorLine={errorLine}
                            errorMessage={errorMessage}
                        />
                    </Form.Item>

                    <Divider />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Form.Item
                            name="sampleInput"
                            label="Sample Input JSON"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        try {
                                            JSON.parse(value);
                                            return Promise.resolve();
                                        } catch (err) {
                                            return Promise.reject('Invalid JSON format');
                                        }
                                    }
                                }
                            ]}
                        >
                            <ContextScriptEditor
                                height="200px"
                                language="json"
                            />
                        </Form.Item>

                        <Form.Item
                            name="expectedOutput"
                            label="Expected Output JSON"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();
                                        try {
                                            JSON.parse(value);
                                            return Promise.resolve();
                                        } catch (err) {
                                            return Promise.reject('Invalid JSON format');
                                        }
                                    }
                                }
                            ]}
                        >
                            <ContextScriptEditor
                                height="200px"
                                language="json"
                            />
                        </Form.Item>
                    </div>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSave}
                                loading={isValidating}
                            >
                                Save
                            </Button>
                            <Button
                                onClick={() => navigate('/notifications/context-scripts')}
                            >
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            {testingVisible && (
                <ScriptTestingPanel
                    visible={testingVisible}
                    onClose={() => setTestingVisible(false)}
                    scriptContent={form.getFieldValue('scriptContent')}
                    sampleInput={form.getFieldValue('sampleInput')}
                    onTestSuccess={handleTestSuccess}
                    form={form}
                />
            )}
        </div>
    );
};